/**
 * The nine messages of the prototype (design/reboot/fixtures.js) as v5 test data: sources, topics,
 * filters and RawItems, plus what the prototype shows for each (`prototype`) so tests can assert the
 * same folders, scores, filter results and timelines.
 *
 * Mapping to v5: 'Website' (Policy Journal) becomes an rss source with format 'reader'; the prototype's
 * 'semantic' model becomes Jev scores; filter conditions are translated to the GroupNode vocabulary.
 * Times are on 2026-10-05 UTC (TEST_EPOCH = 09:42).
 */

import type { RuleInput } from '../contracts/engine.js';
import type { EpochMs, FolderId, SourceId, TagId } from '../contracts/ids.js';
import type { RawItem, SourceKind, SourceFormat } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { sourceId, tagIdForName } from '../kernel/ids.js';
import { foldText } from '../kernel/text.js';

const at = (h: number, m: number): EpochMs => Date.UTC(2026, 9, 5, h, m, 0) as EpochMs;

export type FixtureSourceKey = 'signal' | 'circuit' | 'policy' | 'urban' | 'dispatch';
export type FixtureTopicKey = 'ai' | 'engineering' | 'policy' | 'cities';
export type FixtureFilterKey = 'local-ai' | 'europe-policy' | 'promotions' | 'old-weak';

export interface FixtureSource {
  key: FixtureSourceKey;
  id: SourceId;
  kind: SourceKind;
  locator: string;
  name: string;
  format: SourceFormat;
  pullMinutes: number;
  keepImages: boolean;
  keepVideos: boolean;
  enabled: boolean;
  description: string;
  /** The prototype's usefulness numbers (kept / received). */
  kept: number;
  received: number;
}

const src = (s: Omit<FixtureSource, 'id'>): FixtureSource => ({ ...s, id: sourceId(s.kind, s.locator) });

export const FIXTURE_SOURCES: readonly FixtureSource[] = [
  src({ key: 'signal', kind: 'rss', locator: 'https://signal-review.example/feed.xml', name: 'Signal Review', format: 'original', pullMinutes: 15, keepImages: true, keepVideos: true, enabled: true, description: 'Technology reporting and analysis.', kept: 24, received: 185 }),
  src({ key: 'circuit', kind: 'email', locator: 'imap://reader@mail.example:993/Newsletters', name: 'The Circuit', format: 'original', pullMinutes: 15, keepImages: true, keepVideos: false, enabled: true, description: 'A weekly engineering newsletter.', kept: 19, received: 90 }),
  src({ key: 'policy', kind: 'rss', locator: 'https://policy-journal.example/feed.xml', name: 'Policy Journal', format: 'reader', pullMinutes: 30, keepImages: true, keepVideos: false, enabled: true, description: 'European policy and public services.', kept: 16, received: 410 }),
  src({ key: 'urban', kind: 'telegram', locator: 'telegram:public:urban_notes_sample', name: 'Urban Notes', format: 'original', pullMinutes: 10, keepImages: true, keepVideos: true, enabled: true, description: 'Updates on the places we live.', kept: 8, received: 73 }),
  src({ key: 'dispatch', kind: 'rss', locator: 'https://dispatch.example/rss', name: 'Daily Dispatch', format: 'reader', pullMinutes: 60, keepImages: false, keepVideos: false, enabled: false, description: 'A new source, paused until configured.', kept: 0, received: 0 }),
];

export function fixtureSource(key: FixtureSourceKey): FixtureSource {
  const s = FIXTURE_SOURCES.find((x) => x.key === key);
  if (!s) throw new DigesterError('not_found', `unknown fixture source ${key}`);
  return s;
}

export const FIXTURE_TOPICS: ReadonlyArray<{ key: FixtureTopicKey; name: string; icon: string; color: string }> = [
  { key: 'ai', name: 'Artificial intelligence', icon: 'sparkles', color: '#b3a3ed' },
  { key: 'engineering', name: 'Software engineering', icon: 'code', color: '#8caee2' },
  { key: 'policy', name: 'Europe & policy', icon: 'globe', color: '#9bbd9d' },
  { key: 'cities', name: 'Cities & transport', icon: 'tram-front', color: '#c7b08e' },
];

/** The tag the 'local-ai' filter adds. */
export const FIXTURE_TAG_RESEARCH = { name: 'Research', id: tagIdForName(foldText('Research')) as TagId };

/** The prototype's four filters as RuleInputs (source and tag ids are deterministic). */
export const FIXTURE_FILTERS: ReadonlyArray<{ key: FixtureFilterKey; input: RuleInput }> = [
  {
    key: 'local-ai',
    input: {
      kind: 'filter', name: 'Local AI tools', icon: 'cpu', enabled: true, triggerFolderId: 'inbox' as FolderId,
      condition: { type: 'group', operator: 'all', children: [{ type: 'group', operator: 'any', children: [
        { type: 'condition', field: 'body', operator: 'contains', value: 'local models' },
        { type: 'condition', field: 'title', operator: 'contains', value: 'on-device' },
      ] }] },
      actions: [{ type: 'move', folderId: 'priority' as FolderId }, { type: 'add-tag', tagId: FIXTURE_TAG_RESEARCH.id }],
    },
  },
  {
    key: 'europe-policy',
    input: {
      kind: 'filter', name: 'European digital policy', icon: 'landmark', enabled: true, triggerFolderId: 'inbox' as FolderId,
      condition: { type: 'group', operator: 'all', children: [{ type: 'group', operator: 'all', children: [
        { type: 'condition', field: 'source', operator: 'in', values: [sourceId('rss', 'https://policy-journal.example/feed.xml')] },
        { type: 'condition', field: 'body', operator: 'contains', value: 'digital identity' },
      ] }] },
      actions: [{ type: 'move', folderId: 'priority' as FolderId }],
    },
  },
  {
    key: 'promotions',
    input: {
      kind: 'filter', name: 'Skip promotions', icon: 'ban', enabled: true, triggerFolderId: 'inbox' as FolderId,
      condition: { type: 'group', operator: 'any', children: [{ type: 'group', operator: 'any', children: [
        { type: 'condition', field: 'title', operator: 'contains', value: 'subscriber offer' },
        { type: 'condition', field: 'title', operator: 'contains', value: 'weekend sale' },
      ] }] },
      actions: [{ type: 'move', folderId: 'dismissed' as FolderId }],
    },
  },
  {
    key: 'old-weak',
    input: {
      kind: 'filter', name: 'Clear old weak matches', icon: 'clock', enabled: false, triggerFolderId: 'weak' as FolderId,
      condition: { type: 'group', operator: 'all', children: [{ type: 'group', operator: 'all', children: [
        { type: 'condition', field: 'hours', operator: 'gt', value: 168, unit: 'hours' },
      ] }] },
      actions: [{ type: 'move', folderId: 'dismissed' as FolderId }],
    },
  },
];

export interface FixtureMessage {
  key: string;
  source: FixtureSourceKey;
  raw: RawItem;
  /** What the prototype shows for this message. Topic and filter keys refer to the lists above. */
  prototype: {
    minutes: number;
    folder: 'inbox' | 'priority' | 'weak' | 'dismissed';
    read: boolean;
    topics: Array<{ topic: FixtureTopicKey; score: number }>;
    tags: string[];
    rule: FixtureFilterKey | null;
    results: Partial<Record<FixtureFilterKey, 0 | 1 | null>>;
    assessment: string | null;
    history: Array<{ time: string; type: string; actor: string | null; title: string; detail: string }>;
  };
}

export const FIXTURE_MESSAGES: readonly FixtureMessage[] = [
  {
    key: "m1",
    source: "signal",
    raw: {
      externalId: "urn:fixture:m1",
      url: "https://digester.example/m1",
      title: "Small models are moving onto the device",
      author: "Maya Chen",
      publishedAt: at(9, 42),
      html: "<p>For teams putting AI into daily work, the useful question is becoming more specific: can a small model do one job reliably, on the hardware already in front of them?</p><p>Recent experiments with local models focus on work with clear inputs and outputs: sorting requests, extracting fields, and suggesting labels for internal notes. These tasks make it easier to compare a prediction with a human decision.</p><p>Running a model on the device can keep a workflow available without a connection and make response times more predictable. It also puts updates, evaluation, and hardware limits in the hands of the team using it.</p><p>The important measurement is time to an accepted result. A fast prediction is less valuable if it takes several minutes to correct. Review time, exceptions, and maintenance belong in the same comparison.</p><p>The examples remain small. They offer a starting point for a focused experiment, rather than a universal answer about which model to use.</p>",
      imageUrl: "https://digester.example/assets/workspace.jpg",
    },
    prototype: {
      minutes: 4,
      folder: "priority",
      read: false,
      topics: [{ topic: "ai", score: 92 }, { topic: "engineering", score: 78 }],
      tags: ["Research"],
      rule: "local-ai",
      results: { "local-ai": 1, "europe-policy": 0, "promotions": 0 },
      assessment: "Useful for your interest in practical AI. The examples support narrow tasks; the article does not establish that smaller models replace general assistants.",
      history: [
        { time: "09:42", type: "source", actor: "signal", title: "Received in Inbox", detail: "RSS · Original formatting preserved" },
        { time: "09:42", type: "filter", actor: "local-ai", title: "Inbox → Priority", detail: "Body contains “local models”" },
        { time: "09:42", type: "model", actor: "semantic", title: "Topics assessed", detail: "Artificial intelligence 92 · Software engineering 78" },
      ],
    },
  },
  {
    key: "m2",
    source: "circuit",
    raw: {
      externalId: "urn:fixture:m2",
      url: "https://digester.example/m2",
      title: "Measure the work after the model response",
      author: "Jordan Lee",
      publishedAt: at(9, 31),
      html: "<p>An engineering team found that its most time-consuming step happened after the model finished: someone still had to decide whether the result could be used.</p><p>The team changed its evaluation to measure the time between receiving a document and accepting the final result. Corrections, missing information, and escalation became visible in the same measurement.</p><p>Optimising for completed work changed the team’s priorities. Acceptance rate mattered as much as latency, and clear handling of exceptions mattered more than a polished demonstration.</p>",
    },
    prototype: {
      minutes: 3,
      folder: "priority",
      read: false,
      topics: [{ topic: "ai", score: 87 }, { topic: "engineering", score: 82 }],
      tags: [],
      rule: null,
      results: { "local-ai": 0, "europe-policy": 0, "promotions": 0 },
      assessment: "A useful evaluation principle: measure time to a usable result, including human review, rather than response speed alone.",
      history: [
        { time: "09:31", type: "source", actor: "circuit", title: "Received in Inbox", detail: "Email newsletter" },
        { time: "09:31", type: "filter", actor: null, title: "No rules matched", detail: "3 evaluated · 0 pending" },
        { time: "09:31", type: "model", actor: "semantic", title: "Inbox → Priority", detail: "Artificial intelligence 87 ≥ threshold 70" },
      ],
    },
  },
  {
    key: "m3",
    source: "policy",
    raw: {
      externalId: "urn:fixture:m3",
      url: "https://digester.example/m3",
      title: "Digital identity moves into everyday services",
      author: "Lena Müller",
      publishedAt: at(9, 18),
      html: "<p>A digital identity pilot is turning from demonstrations to everyday tasks, including signing documents and proving eligibility for a service.</p><p>The trial will also examine account recovery, support, and accessibility. These parts of a service are easy to overlook when the initial demonstration works well.</p><p>The report describes a trial. Participation requirements and broader availability remain decisions for a later stage.</p>",
    },
    prototype: {
      minutes: 3,
      folder: "priority",
      read: false,
      topics: [{ topic: "policy", score: 91 }, { topic: "engineering", score: 43 }],
      tags: [],
      rule: "europe-policy",
      results: { "local-ai": 0, "europe-policy": 1, "promotions": 0 },
      assessment: "Relevant to digital services and privacy. The report describes a limited pilot, not a completed public rollout.",
      history: [
        { time: "09:18", type: "source", actor: "policy", title: "Received in Inbox", detail: "Website · Reader text extracted" },
        { time: "09:18", type: "filter", actor: "europe-policy", title: "Inbox → Priority", detail: "Source is Policy Journal AND body contains “digital identity”" },
      ],
    },
  },
  {
    key: "m4",
    source: "urban",
    raw: {
      externalId: "urn:fixture:m4",
      url: "https://digester.example/m4",
      title: "A better timetable can change a whole journey",
      author: "Sam Rivera",
      publishedAt: at(8, 56),
      html: "<p>A transport working group is studying the minutes lost between services. A reliable connection can matter as much as the speed of either vehicle.</p><p>The group is comparing practical changes: simpler stopping patterns, coordinated departures, and clearer information when a connection is missed.</p><p>The next review will look at the entire journey, including the walk to the stop and the final connection.</p>",
      imageUrl: "https://digester.example/assets/tram.jpg",
    },
    prototype: {
      minutes: 2,
      folder: "priority",
      read: true,
      topics: [{ topic: "cities", score: 84 }, { topic: "policy", score: 38 }],
      tags: [],
      rule: null,
      results: { "local-ai": 0, "europe-policy": 0, "promotions": 0 },
      assessment: null,
      history: [
        { time: "08:56", type: "source", actor: "urban", title: "Received in Inbox", detail: "Telegram · Photo included" },
        { time: "08:56", type: "filter", actor: null, title: "No rules matched", detail: "3 evaluated · 0 pending" },
        { time: "08:56", type: "model", actor: "semantic", title: "Inbox → Priority", detail: "Cities & transport 84 ≥ threshold 70" },
        { time: "09:02", type: "user", actor: null, title: "Read by you", detail: "" },
      ],
    },
  },
  {
    key: "m5",
    source: "circuit",
    raw: {
      externalId: "urn:fixture:m5",
      url: "https://digester.example/m5",
      title: "Smaller releases make dependency updates easier",
      author: "Jordan Lee",
      publishedAt: at(8, 40),
      html: "<p>A maintainer shares a short checklist for predictable dependency updates: smaller changes, a known rollback path, and migration notes that explain what actually changed.</p><p>Keeping the update small makes it easier to understand a failure. It also makes review more useful, because a reviewer can connect the dependency change with the behaviour being tested.</p>",
    },
    prototype: {
      minutes: 4,
      folder: "priority",
      read: false,
      topics: [{ topic: "engineering", score: 89 }, { topic: "ai", score: 32 }],
      tags: [],
      rule: null,
      results: { "local-ai": 0, "europe-policy": 0, "promotions": 0 },
      assessment: null,
      history: [
        { time: "08:40", type: "source", actor: "circuit", title: "Received in Inbox", detail: "Email newsletter" },
        { time: "08:40", type: "model", actor: "semantic", title: "Inbox → Priority", detail: "Software engineering 89 ≥ threshold 70" },
      ],
    },
  },
  {
    key: "m6",
    source: "signal",
    raw: {
      externalId: "urn:fixture:m6",
      url: "https://digester.example/m6",
      title: "New tools for reading public datasets",
      author: "Maya Chen",
      publishedAt: at(8, 22),
      html: "<p>A collection of small tools aims to make public datasets easier to inspect. The tools focus on previews, consistent column descriptions, and identifying missing values.</p><p>This message is waiting for its first routing evaluation.</p>",
    },
    prototype: {
      minutes: 2,
      folder: "inbox",
      read: false,
      topics: [],
      tags: [],
      rule: null,
      results: { "local-ai": null, "europe-policy": null, "promotions": null },
      assessment: null,
      history: [
        { time: "08:22", type: "source", actor: "signal", title: "Received in Inbox", detail: "Routing pending" },
      ],
    },
  },
  {
    key: "m7",
    source: "dispatch",
    raw: {
      externalId: "urn:fixture:m7",
      url: "https://digester.example/m7",
      title: "A new source joins your reading list",
      publishedAt: at(8, 10),
      html: "<p>This sample message shows how a new source appears before it has reading history, topic scores, or a written assessment.</p>",
    },
    prototype: {
      minutes: 1,
      folder: "inbox",
      read: false,
      topics: [],
      tags: [],
      rule: null,
      results: { "local-ai": null, "europe-policy": null, "promotions": null },
      assessment: null,
      history: [
        { time: "08:10", type: "source", actor: "dispatch", title: "Received in Inbox", detail: "New source · Evaluation pending" },
      ],
    },
  },
  {
    key: "m8",
    source: "signal",
    raw: {
      externalId: "urn:fixture:m8",
      url: "https://digester.example/m8",
      title: "What shared offices get wrong about quiet space",
      author: "Maya Chen",
      publishedAt: at(7, 48),
      html: "<p>Workspace operators are testing clearer boundaries between collaborative areas and quiet work. The report focuses on the decisions people make about where to sit throughout the day.</p>",
    },
    prototype: {
      minutes: 3,
      folder: "weak",
      read: false,
      topics: [{ topic: "engineering", score: 29 }, { topic: "cities", score: 21 }],
      tags: [],
      rule: null,
      results: { "local-ai": 0, "europe-policy": 0, "promotions": 0 },
      assessment: null,
      history: [
        { time: "07:48", type: "source", actor: "signal", title: "Received in Inbox", detail: "RSS" },
        { time: "07:48", type: "model", actor: "semantic", title: "Inbox → Weak", detail: "Highest topic score 29 < threshold 70" },
      ],
    },
  },
  {
    key: "m9",
    source: "urban",
    raw: {
      externalId: "urn:fixture:m9",
      url: "https://digester.example/m9",
      title: "Subscriber offer: the weekend reading bundle",
      publishedAt: at(7, 31),
      html: "<p>This sample promotional message was moved to Dismissed by a rule. Its history remains available, and you can return it to any folder.</p>",
    },
    prototype: {
      minutes: 1,
      folder: "dismissed",
      read: true,
      topics: [],
      tags: [],
      rule: "promotions",
      results: { "local-ai": 0, "europe-policy": 0, "promotions": 1 },
      assessment: null,
      history: [
        { time: "07:31", type: "source", actor: "urban", title: "Received in Inbox", detail: "Telegram" },
        { time: "07:31", type: "filter", actor: "promotions", title: "Inbox → Dismissed", detail: "Title contains “subscriber offer” · Semantic model skipped" },
      ],
    },
  },
];
